import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Gift, Trophy, Star, TrendingUp, Share2,
  RefreshCw, Loader2, PartyPopper, Check, Sparkles,
  Crown, Target, Award, Heart,
} from "lucide-react";
import { API, axios } from "@/api";
import { BrandLogo } from "@/components/shared/BrandLogo";
import { toast } from "sonner";

function StatCard({ icon: Icon, label, value, sub, color, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      className="bg-white rounded-2xl p-4 shadow-[0_2px_12px_rgba(0,0,0,0.05)] border border-slate-100"
      data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${color}15` }}
        >
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">{label}</p>
          <p className="text-xl font-extrabold text-slate-800">{value}</p>
          {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
        </div>
      </div>
    </motion.div>
  );
}

function CategoryBar({ name, count, maxCount, delay }) {
  const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay, duration: 0.3 }}
      className="flex items-center gap-3"
    >
      <span className="text-xs font-semibold text-slate-600 w-28 truncate">{name}</span>
      <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ delay: delay + 0.2, duration: 0.5, ease: "easeOut" }}
          className="h-full rounded-full bg-gradient-to-r from-sky-400 to-sky-500"
        />
      </div>
      <span className="text-xs font-bold text-slate-500 w-6 text-right">{count}</span>
    </motion.div>
  );
}

function ClaimedDealRow({ deal, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.3 }}
      className="flex items-center gap-3 py-2.5 border-b border-slate-50 last:border-0"
      data-testid={`claimed-deal-${deal.business_id}`}
    >
      <BrandLogo name={deal.name} logoUrl={deal.logo_url} size="sm" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-slate-800 truncate">{deal.name}</p>
        <p className="text-[10px] text-slate-400 truncate">{deal.offer_title || deal.category}</p>
      </div>
      <div className="text-right flex-shrink-0">
        {deal.value > 0 && (
          <p className="text-sm font-extrabold text-emerald-600">${deal.value}</p>
        )}
        {deal.rating && (
          <div className="flex gap-0.5 justify-end mt-0.5">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className={`w-2.5 h-2.5 ${i < deal.rating ? "text-amber-400 fill-amber-400" : "text-slate-200"}`} />
            ))}
          </div>
        )}
      </div>
      <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
    </motion.div>
  );
}

export default function BirthdaySummaryPage() {
  const { year } = useParams();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const summaryRef = useRef(null);

  const targetYear = parseInt(year) || new Date().getFullYear();

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const res = await axios.get(`${API}/birthday-summary/${targetYear}`);
        setSummary(res.data);
      } catch (e) {
        if (e.response?.status === 400) {
          toast.error(e.response?.data?.detail || "Could not generate summary");
        } else {
          toast.error("Failed to load summary");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, [targetYear]);

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const res = await axios.post(`${API}/birthday-summary/${targetYear}/regenerate`);
      setSummary(res.data);
      toast.success("Summary refreshed!");
    } catch {
      toast.error("Failed to regenerate");
    } finally {
      setRegenerating(false);
    }
  };

  const handleShare = async () => {
    const shareText = summary
      ? `My ${targetYear} BirthdayDeaLight Summary: ${summary.stats?.deals_claimed || 0} deals claimed, $${summary.stats?.total_value_claimed || 0} saved! Check out BirthdayDeaLight!`
      : "Check out my BirthdayDeaLight Summary!";

    if (navigator.share) {
      try {
        await navigator.share({
          title: `My ${targetYear} Birthday Summary`,
          text: shareText,
          url: window.location.href,
        });
      } catch {
        // User cancelled
      }
    } else {
      await navigator.clipboard.writeText(shareText);
      toast.success("Summary text copied to clipboard!");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="min-h-screen pb-24" style={{ backgroundColor: "var(--theme-page-bg, #ffffff)" }} data-testid="birthday-summary-empty">
        <div className="bg-gradient-to-br from-slate-700 to-slate-900 text-white px-4 pt-12 pb-8">
          <button onClick={() => navigate(-1)} className="absolute top-4 left-4 w-9 h-9 rounded-full bg-white/15 flex items-center justify-center" data-testid="summary-back-btn">
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div className="text-center">
            <Gift className="w-10 h-10 mx-auto mb-2 text-slate-400" />
            <h1 className="text-2xl font-extrabold">No Summary Yet</h1>
            <p className="text-slate-400 text-sm mt-1">Your {targetYear} birthday summary will appear after your birthday window</p>
          </div>
        </div>
        <div className="px-4 pt-6 text-center">
          <p className="text-sm text-slate-500">Claim some deals during your birthday window and come back!</p>
          <button
            onClick={() => navigate("/")}
            className="mt-4 px-6 py-3 rounded-xl bg-sky-500 text-white font-bold text-sm"
            data-testid="summary-browse-btn"
          >
            Browse Deals
          </button>
        </div>
      </div>
    );
  }

  const { stats, claimed_details, highlight } = summary;
  const catBreakdown = stats?.category_breakdown || {};
  const maxCatCount = Math.max(...Object.values(catBreakdown), 1);

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: "var(--theme-page-bg, #ffffff)" }} data-testid="birthday-summary-page" ref={summaryRef}>
      {/* Hero */}
      <div className="bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700 text-white px-4 pt-12 pb-10 relative overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-white/5 -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full bg-white/5 translate-y-1/2 -translate-x-1/2" />

        <button onClick={() => navigate(-1)} className="absolute top-4 left-4 w-9 h-9 rounded-full bg-white/15 flex items-center justify-center z-10" data-testid="summary-back-btn">
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>

        <div className="relative text-center">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 300, damping: 20 }}>
            <PartyPopper className="w-12 h-12 mx-auto mb-3 text-amber-300" />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-2xl font-extrabold tracking-tight"
          >
            {summary.user_name}'s {targetYear} Birthday
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-purple-200 text-sm mt-1"
          >
            {summary.birthday_date}
          </motion.p>

          {/* Big value display */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, type: "spring" }}
            className="mt-6"
          >
            <p className="text-[11px] font-medium text-purple-300 uppercase tracking-wider">Total Birthday Haul</p>
            <p className="text-5xl font-black mt-1" data-testid="summary-total-value">
              ${stats?.total_value_claimed || 0}
            </p>
            <p className="text-sm text-purple-200 mt-1">
              across {stats?.deals_claimed || 0} deal{(stats?.deals_claimed || 0) !== 1 ? 's' : ''}
            </p>
          </motion.div>

          {/* Level badge */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 text-[11px] font-bold"
          >
            <Crown className="w-3.5 h-3.5 text-amber-300" />
            {summary.user_level}
          </motion.div>
        </div>
      </div>

      {/* Highlight quote */}
      {highlight && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mx-4 -mt-5 bg-white rounded-2xl p-4 shadow-lg border border-purple-100 relative z-10"
          data-testid="summary-highlight"
        >
          <div className="flex items-start gap-2.5">
            <Sparkles className="w-5 h-5 text-violet-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-700 leading-relaxed font-medium">{highlight}</p>
          </div>
        </motion.div>
      )}

      {/* Stats Grid */}
      <div className="px-4 mt-5">
        <div className="grid grid-cols-2 gap-3">
          <StatCard
            icon={Gift}
            label="Deals Claimed"
            value={stats?.deals_claimed || 0}
            sub={stats?.all_time_claimed > 0 ? `${stats.all_time_claimed} all-time` : null}
            color="#8b5cf6"
            delay={0.1}
          />
          <StatCard
            icon={Target}
            label="Plan Completion"
            value={`${stats?.completion_rate || 0}%`}
            sub={`${stats?.planned_claimed || 0} of ${stats?.planned_count || 0} planned`}
            color="#0ea5e9"
            delay={0.15}
          />
          <StatCard
            icon={TrendingUp}
            label="Points Earned"
            value={stats?.points_earned || 0}
            sub="during birthday window"
            color="#10b981"
            delay={0.2}
          />
          <StatCard
            icon={Award}
            label="Verifications"
            value={stats?.verifications_made || 0}
            sub="community contributions"
            color="#f59e0b"
            delay={0.25}
          />
        </div>
      </div>

      {/* Category Breakdown */}
      {Object.keys(catBreakdown).length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="px-4 mt-5"
        >
          <div className="bg-white rounded-2xl p-4 shadow-[0_2px_12px_rgba(0,0,0,0.05)] border border-slate-100" data-testid="summary-category-breakdown">
            <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-500" />
              Categories Covered
            </h3>
            <div className="space-y-2.5">
              {Object.entries(catBreakdown).map(([cat, count], i) => (
                <CategoryBar key={cat} name={cat} count={count} maxCount={maxCatCount} delay={0.35 + i * 0.05} />
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* Claimed Deals List */}
      {claimed_details && claimed_details.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="px-4 mt-5"
        >
          <div className="bg-white rounded-2xl p-4 shadow-[0_2px_12px_rgba(0,0,0,0.05)] border border-slate-100" data-testid="summary-claimed-deals">
            <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-500" />
              Deals You Claimed
            </h3>
            <div>
              {claimed_details.map((deal, i) => (
                <ClaimedDealRow key={deal.business_id} deal={deal} delay={0.45 + i * 0.03} />
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* MVP Deal highlight */}
      {stats?.best_deal_name && stats?.best_deal_value > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="px-4 mt-5"
        >
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-4 border border-amber-200" data-testid="summary-mvp-deal">
            <div className="flex items-center gap-2 mb-1">
              <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
              <span className="text-[11px] font-bold text-amber-600 uppercase tracking-wider">MVP Deal</span>
            </div>
            <p className="text-base font-extrabold text-slate-800">{stats.best_deal_name}</p>
            <p className="text-sm text-amber-600 font-bold">${stats.best_deal_value} value</p>
          </div>
        </motion.div>
      )}

      {/* Action Buttons */}
      <div className="px-4 mt-6 space-y-2">
        <button
          onClick={handleShare}
          className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-violet-500 text-white font-bold text-sm shadow-md hover:bg-violet-600 transition-colors"
          data-testid="summary-share-btn"
        >
          <Share2 className="w-4 h-4" /> Share My Summary
        </button>
        <button
          onClick={handleRegenerate}
          disabled={regenerating}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50 transition-colors disabled:opacity-50"
          data-testid="summary-regenerate-btn"
        >
          {regenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Refresh Summary
        </button>
      </div>

      {/* Wishlist & Favorites mini stats */}
      <div className="px-4 mt-5 mb-6">
        <div className="flex gap-3">
          <div className="flex-1 bg-white rounded-xl p-3 border border-slate-100 text-center">
            <Heart className="w-4 h-4 text-rose-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-slate-800">{stats?.favorites_count || 0}</p>
            <p className="text-[10px] text-slate-400">Favorites</p>
          </div>
          <div className="flex-1 bg-white rounded-xl p-3 border border-slate-100 text-center">
            <Gift className="w-4 h-4 text-violet-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-slate-800">{stats?.wishlist_count || 0}</p>
            <p className="text-[10px] text-slate-400">Wishlisted</p>
          </div>
        </div>
      </div>
    </div>
  );
}
